import type { SortDirection, Table as TableType } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { ArrowRight, ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';

import type { StaticTableColumnMeta } from './types';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn/table';
import { formatNumber } from '@/shared/utils/format-number';
import { ToggleStage } from '../stage/toggle-stage';
import { TableSkeleton } from '../table-skeleton/table-skeleton';

export function StaticTable<RowData>({
  isLoading,
  table,
  onRowClick,
  pageSize = 5,
  minHeight,
  gridTemplateColumns,
}: {
  isLoading: boolean;
  onRowClick?: (data: RowData, event: React.MouseEvent) => void;
  pageSize: number;
  minHeight?: string;
  table: TableType<RowData>;
  gridTemplateColumns: string[];
}) {
  const skeletonColumns = useMemo(() => {
    return gridTemplateColumns.map((column) => {
      if (column.includes('minmax')) {
        const size = parseInt(column.split('minmax(')[1].split(')')[0]);
        return { className: `w-[${size}px]` };
      }
      if (column.includes('fr')) {
        return { className: 'w-full' };
      }
      return { className: `w-[${column}]` };
    });
  }, [gridTemplateColumns]);

  return (
    <div style={{ minHeight: minHeight ?? `${pageSize * 50 + 20}px` }}>
      <ToggleStage
        status={!isLoading}
        fallback={<TableSkeleton columns={skeletonColumns} rows={pageSize} rowHeight="h-[72px]" />}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} gridTemplateColumns={gridTemplateColumns.join(' ')}>
                {headerGroup.headers.map((header) => {
                  const headerNode = flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  );
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : header.column.columnDef.enableSorting ? (
                        <SortableHeader
                          currentSort={header.column.getIsSorted()}
                          onSortToggle={() => {
                            const currentSort = header.column.getIsSorted();
                            switch (currentSort) {
                              case 'asc':
                                header.column.toggleSorting(true);
                                break;
                              case 'desc':
                                header.column.toggleSorting(false);
                                break;
                              default:
                                header.column.toggleSorting(true);
                                break;
                            }
                          }}
                        >
                          {headerNode}
                        </SortableHeader>
                      ) : (
                        headerNode
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                gridTemplateColumns={gridTemplateColumns.join(' ')}
                className={onRowClick ? 'cursor-pointer hover:bg-zinc-50' : ''}
                onClick={onRowClick ? (event) => onRowClick(row.original, event) : undefined}
              >
                {row.getVisibleCells().map((cell) => {
                  const tableCellClassName = (cell.column.columnDef.meta as StaticTableColumnMeta)
                    ?.tableCellClassName;
                  return (
                    <TableCell key={cell.id} className={tableCellClassName}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ToggleStage>
    </div>
  );
}

StaticTable.Cell = function Cell({ getValue }: { getValue: () => string | number }) {
  const value = getValue();

  return (
    <span className={`text-base-foreground block truncate font-normal`} title={value.toString()}>
      {typeof value === 'number' ? formatNumber(value) : value}
    </span>
  );
};

StaticTable.Header = function TableHeader({
  title,
  subtitle,
  onRightClick,
}: {
  title: string;
  subtitle?: string;
  onRightClick?: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-zinc-950">{title}</h3>
          {subtitle && <p className="text-sm text-zinc-500">{subtitle}</p>}
        </div>
      </div>
      {onRightClick != null && (
        <button
          className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-zinc-100"
          onClick={onRightClick}
        >
          <ArrowRight className="text-base-foreground h-4 w-4" />
        </button>
      )}
    </div>
  );
};

function SortableHeader({
  children,
  onSortToggle,
  currentSort,
}: {
  children: ReactNode;
  onSortToggle?: () => void;
  currentSort?: SortDirection | false;
}) {
  return (
    <div className="flex items-center">
      {children}
      <button
        className="ml-1 inline-flex h-4 w-4 items-center justify-center"
        onClick={onSortToggle}
      >
        {!currentSort ? (
          <ChevronsUpDownIcon className="h-3 w-3 text-zinc-500" />
        ) : currentSort === 'asc' ? (
          <ChevronUpIcon className="h-3 w-3 text-zinc-500" />
        ) : (
          <ChevronDownIcon className="h-3 w-3 text-zinc-500" />
        )}
      </button>
    </div>
  );
}
